import { Button, DialogActions } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  BaseSingleInputFieldProps,
  DatePicker,
  DateValidationError,
  FieldSection,
  LocalizationProvider,
  PickersActionBarProps,
  UseDateFieldProps,
} from '@mui/x-date-pickers';
import { useLocaleText } from '@mui/x-date-pickers/internals';
import { AnyAction } from '@reduxjs/toolkit';
import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from '../../redux/hooks';
import { formatDateTo_MMM_dd_yyyy } from '../../utils';
import { DateTime } from 'luxon';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

function CustomDatePicker({
  dateFilterValueLocal,
  setDateFilter,
  inputView,
  buttonSx,
  buttonColor,
  views,
  openTo,
  maxDate,
  isQuarterlyForecast,
}: {
  dateFilterValueLocal: string | null;
  setDateFilter: {
    // We are supposed to store date in the slice, this method is for updating temporary date value
    // It is changed on alsmot any interaction with the Date Picker
    func: (value: string | null) => AnyAction;
    // This one is only fired when the chosen date is confirmed. You should track this one, when refetching data
    setGlobalFunc: () => AnyAction;
    clean: () => AnyAction;
    type: 'dispatch';
  };
  inputView?: boolean;
  buttonSx?: any;
  buttonColor?: 'primary' | 'secondary';
  views?: ('year' | 'month' | 'day')[];
  openTo?: 'year' | 'month' | 'day';
  maxDate?: Date;
  isQuarterlyForecast?: boolean;
}) {
  const dispatch = useAppDispatch();

  const [isDateFilterOpen, setDateFilterOpen] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState<Date | null>(null);

  React.useEffect(() => {
    setInternalValue(
      dateFilterValueLocal === null
        ? null
        : DateTime.fromFormat(dateFilterValueLocal, 'yyyy-MM-dd').toJSDate(),
    );
  }, [dateFilterValueLocal, isDateFilterOpen]);

  const handleDateFilterOnClick = useCallback(() => {
    setDateFilterOpen((prev) => !prev);
  }, [setDateFilterOpen]);

  const handleDateFilterOnClose = useCallback(() => {
    setDateFilterOpen(false);
  }, []);

  const onDateFilterChange = useCallback((newValue: any) => {
    setInternalValue(newValue);
  }, []);

  const handleDateFilterOnAccept = useCallback(
    (newValue: any) => {
      if (newValue instanceof Date && !isNaN(newValue as any)) {
        dispatch(
          setDateFilter.func(
            DateTime.fromJSDate(newValue).toFormat('yyyy-MM-dd'),
          ),
        );
        dispatch(setDateFilter.setGlobalFunc());
      }
      setDateFilterOpen(false);
    },
    [dispatch, setDateFilter],
  );

  const onDateFilterClear = useCallback(() => {
    dispatch(setDateFilter.clean());
    setInternalValue(null);
  }, [dispatch, setDateFilter]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      dateFormats={{
        monthShort: "MMM 'Q'q", // date-fns: 'q' returns quarter number, so "'Q'q" displays as "Q1", "Q2", etc.
      }}
    >
      <DatePicker
        open={isDateFilterOpen}
        views={views || ['year', 'month', 'day']}
        openTo={openTo || 'year'}
        format="MM dd yyyy"
        maxDate={maxDate}
        shouldDisableMonth={(date: Date) => {
          // Only enable March, June, September, December for quarterly
          return isQuarterlyForecast
            ? ![2, 5, 8, 11].includes(date.getMonth())
            : false;
        }}
        value={internalValue}
        label={
          dateFilterValueLocal === null
            ? 'Today'
            : formatDateTo_MMM_dd_yyyy(
                DateTime.fromFormat(
                  dateFilterValueLocal,
                  'yyyy-MM-dd',
                ).toJSDate(),
              )
        }
        slots={{
          ...(!inputView && { field: DateFiltersButton as any }),
          actionBar: CustomActionBar as any,
        }}
        slotProps={{
          ...(!inputView && {
            field: {
              setOpen: handleDateFilterOnClick,
              variant: dateFilterValueLocal ? 'contained' : 'text',
              ...(buttonColor && { color: buttonColor }),
              sx: {
                ...{ color: dateFilterValueLocal ? 'white' : 'black' },
                ...(!!buttonSx && buttonSx),
              },
              isQuarterlyForecast,
              dateValue: dateFilterValueLocal,
            } as any,
          }),
          actionBar: {
            onClearCustom: onDateFilterClear,
          } as any,
        }}
        onChange={onDateFilterChange}
        onAccept={handleDateFilterOnAccept}
        onClose={handleDateFilterOnClose}
      />
    </LocalizationProvider>
  );
}

interface ButtonFieldProps
  extends UseDateFieldProps<any>,
    BaseSingleInputFieldProps<
      any | null,
      any,
      FieldSection,
      DateValidationError
    > {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;

  variant: 'text' | 'outlined' | 'contained';
  color: 'primary' | 'secondary';
  sx: any;
  isQuarterlyForecast?: boolean;
  dateValue?: string | null;
}

function DateFiltersButton(props: ButtonFieldProps) {
  const {
    setOpen,
    variant,
    label,
    color,
    sx,
    InputProps: { ref } = {},
    isQuarterlyForecast = false,
    dateValue,
  } = props;

  const quarterLabel = useMemo(() => {
    if (!dateValue) return 'All Q forecast';
    try {
      const date = DateTime.fromFormat(dateValue, 'yyyy-MM-dd');
      if (!date.isValid) {
        return 'All Q forecast';
      }
      const quarter = Math.ceil(date.month / 3);
      const year = date.year;
      return `Q${quarter} ${year} forecast`;
    } catch {
      return 'All Q forecast';
    }
  }, [dateValue]);

  return (
    <Button
      variant={variant}
      sx={sx}
      ref={ref}
      color={color}
      onClick={() => setOpen?.((prev) => !prev)}
      startIcon={isQuarterlyForecast ? <CalendarTodayIcon /> : undefined}
    >
      {isQuarterlyForecast
        ? quarterLabel
        : label
        ? `Date: ${label}`
        : 'Pick a date'}
    </Button>
  );
}

interface CustomPickersActionBarProps extends PickersActionBarProps {
  onClearCustom: () => void;
}

function CustomActionBar(props: CustomPickersActionBarProps) {
  const { onAccept, onCancel, onClearCustom, onClear, className } = props;
  const localeText = useLocaleText();

  return (
    <DialogActions className={className}>
      <Button
        data-mui-test="today-action-button"
        variant="text"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        }}
        sx={{
          borderRadius: '100px',
          color: 'rgba(0, 0, 0, 0.87)',
          marginRight: 'auto',
        }}
        onClick={() => {
          onClear();
          onClearCustom();
        }}
        key={'today'}
      >
        {localeText.todayButtonLabel}
      </Button>
      <Button onClick={onCancel} key={'cancel'}>
        {localeText.cancelButtonLabel}
      </Button>
      <Button onClick={onAccept} key={'accept'}>
        {localeText.okButtonLabel}
      </Button>
    </DialogActions>
  );
}

export default CustomDatePicker;
